import { useEffect, useRef, useState } from "react";
/** Helpers */
import clsx from "clsx";
import uniqBy from "lodash/uniqBy";
import { useTranslation } from "react-i18next";
/** MUI */
import { Alert, Box, Button, ButtonGroup, Collapse, IconButton, MenuItem, MenuList, Popover } from "@mui/material";
import { makeStyles } from "@mui/styles";
/** - icons */
import { Add, ArrowDropDown, Close, Remove } from "@mui/icons-material";

import AppTooltip from "@/src/_common/AppTooltip";

const useStyles = makeStyles((theme) => ({
    addButton: {
        color: theme.palette.primary.main,
        backgroundColor: "transparent",
        height: 51,
        "&:focus-within": {
            outlineStyle: "none",
        },
        "&:focus-visible": {
            outlineStyle: "auto",
        },
        [theme.breakpoints.down("lg")]: {
            minWidth: "unset",
            // width: 48,
            height: 48,
            padding: 0,
        },
    },
    addButtonStartIcon: {
        [theme.breakpoints.down("lg")]: {
            margin: 0,
            "& > *:nth-of-type(1)": {
                fontSize: 24,
            },
        },
    },
    popperRoot: {
        zIndex: 1,
    },
    menuPaper: {
        ...theme.shape.boxedWrapper,
        marginTop: -51,
        marginLeft: 2,
        padding: theme.spacing(1, 2, 1.5, 2),
        // borderTopLeftRadius: 0,
        borderColor: "transparent",
        boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
        width: 1014,
        // maxHeight: "75vh",
    },
    menuList: {
        // columns: "280px 3",
        gap: 16,
    },
    menuItemRoot: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(1, 1, 1, 2),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "& + &": {
            marginTop: theme.spacing(2),
        },
        "&.Mui-focusVisible": {
            backgroundColor: "rgba(116, 79, 172, 0.2)",
            outlineStyle: "solid",
            outlineOffset: 0,
            color: theme.palette.primary.dark,
            fontWeight: 500,
        },
    },
    menuItemSelected: {
        backgroundColor: "rgba(116, 79, 172, 0.05)",
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    menuItemFocusVisible: {},
    button: {
        height: 40,
        minWidth: 100,
    },
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        border: 0,
    },
}));

const AddLevels = (props) => {
    const { onAddLevel, onUpdateLevels, levels, choises } = props;

    const _choisesMaxString = choises?.toSorted((a, b) => b?.title?.length - a?.title?.length)?.[0]?.title?.length;
    let _cols = 3;
    switch (true) {
        case _choisesMaxString >= 24 && _choisesMaxString <= 48:
            _cols = 2;
            break;
        case _choisesMaxString > 48:
            _cols = 1;
            break;
        default:
            break;
    }

    const classes = useStyles();
    const { t } = useTranslation();
    const addRef = useRef();
    const multiAddRef = useRef();
    const applyRef = useRef();

    /** states */
    const [selected, setSelected] = useState([]);
    const [prevSelected, setPrevSelected] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [TOOLTIP, setTOOLTIP] = useState(false);
    const [SHOW_INFO, setSHOW_INFO] = useState(false);

    const open = Boolean(anchorEl);

    /** effects */
    useEffect(() => {
        reselect();
    }, [choises, levels]);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);

    const handleClick = (event, value) => {
        const _from = event?.currentTarget?.nodeName;

        if (_from === "BUTTON") {
            event?.preventDefault();
            event?.stopPropagation();
        }

        if (value?.title_orig) {
            select(value.title_orig);
            if (event?.keyCode === 32 || event?.keyCode === 9 || _from === "BUTTON") return;
        }
    };

    const handleClose = ({ event }) => {
        if (event?.keyCode === 9) {
            if (applyRef?.current) {
                applyRef?.current?.focuc();
            }
            return;
        }
        reselect();

        setAnchorEl(null);
    };

    const handleApply = () => {
        const _newChoise = uniqBy(
            [
                ...levels?.filter((level) => selected?.includes(level.title_orig)),
                ...choises?.filter((choise) => selected?.includes(choise.title_orig)),
            ],
            "title_orig"
        )?.map((c) => ({ ...c, order: c.order || "asc" }));

        onUpdateLevels && onUpdateLevels(_newChoise);
        setAnchorEl(null);
    };

    const handleAdd = (event) => {

        event?.preventDefault();
        event?.stopPropagation();
        onAddLevel && onAddLevel();

        if(levels?.length + 1 === choises?.length && multiAddRef?.current?.focus){
            multiAddRef?.current?.focus()
        }
    };

    const reselect = () => {
        const _newSelected = choises
            ?.filter((choise) => {
                return levels?.some((level) => level?.title_orig === choise?.title_orig);
            })
            ?.map((choise) => choise?.title_orig)
            ?.sort();

        setSelected(_newSelected);
        setPrevSelected(_newSelected);

        return _newSelected;
    };

    const select = (id) => {
        const _newSelected = selected?.includes(id) ? selected?.filter((s) => s !== id) : [...selected, id];
        setSelected(_newSelected.sort());
        return _newSelected;
    };

    return (
        <>
            <ButtonGroup
                variant={"outlined"}
                size={"small"}
                sx={{ borderRadius: 26, "& .MuiButtonGroup-grouped:not(:first-of-type)": { ml: 0.125 } }}
            >
                <Button
                    ref={(ref) => {
                        if (ref) {
                            if (!addRef.current) {
                                ref.focus();
                                setTimeout(() => {
                                    addRef.current = true;
                                }, 100);
                            }
                        }
                    }}
                    autoFocus={true}
                    variant={"contained"}
                    classes={{ root: classes.addButton, startIcon: classes.addButtonStartIcon }}
                    startIcon={<Add />}
                    onClick={handleAdd}
                    color={"inherit"}
                    disableRipple
                    disabled={levels?.length >= choises?.length}
                >
                    {t("Dodaj poziom")}
                </Button>
                <AppTooltip
                    title={t("Dodaj / usuń kilka poziomów")}
                    arrow
                    placement={"top"}
                    type={"dark"}
                    open={!!(TOOLTIP === "add")}
                    onOpen={() => setTOOLTIP("add")}
                    onClose={() => setTOOLTIP(false)}
                    onKeyDown={(event) => {
                        if (event?.keyCode === 27) setTOOLTIP(false);
                    }}
                >
                    <Button
                        ref={multiAddRef}
                        variant={"contained"}
                        classes={{ root: classes.addButton, startIcon: classes.addButtonStartIcon }}
                        onClick={handleOpen}
                        color={"inherit"}
                        disableRipple
                    >
                        <ArrowDropDown />
                    </Button>
                </AppTooltip>
            </ButtonGroup>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={(event) => handleClose({ event })}
                classes={{ paper: classes.menuPaper }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                disableEscapeKeyDown
            >
                <>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1 }}>
                        <AppTooltip
                            title={t("Zamknij okno")}
                            arrow
                            placement={"top"}
                            type={"dark"}
                            open={!!(TOOLTIP === "close")}
                            onOpen={() => setTOOLTIP("close")}
                            onClose={() => setTOOLTIP(false)}
                            onKeyDown={(event) => {
                                if (event?.keyCode === 27) {
                                    event?.preventDefault();
                                    event?.stopPropagation();
                                    setTOOLTIP(false);
                                }
                            }}
                        >
                            <IconButton onClick={handleClose} size={"small"} color={"primary"}>
                                <Close />
                            </IconButton>
                        </AppTooltip>
                    </Box>

                    <p className={classes.srOnly}>
                        {t("Naciśnij TAB, aby wejść w tryb dodawania i usuwania poziomów.")}
                    </p>

                    <Box sx={{ maxHeight: "66vh", overflow: "hidden", overflowY: "auto", px: 0.5 }}>
                        <MenuList
                            autoFocusItem={open}
                            id="composition-menu"
                            aria-labelledby="addlevel-note"
                            className={classes.menuList}
                            sx={{
                                // columns: `280px ${_cols}`
                                columnCount: _cols,
                            }}
                        >
                            {choises?.map((choise, i) => {
                                const _selected = selected?.includes(choise.title_orig);
                                return (
                                    <MenuItem
                                        key={choise.id}
                                        onClick={(e) => handleClick(e, choise)}
                                        classes={{
                                            root: clsx(classes.menuItemRoot, _selected && classes.menuItemSelected),
                                            focusVisible: classes.menuItemFocusVisible,
                                        }}
                                        aria-selected={true}
                                        aria-checked={_selected}
                                        role={"option"}
                                        aria-describedby={"addlevel-note"}
                                        aria-label={
                                            choise.title +
                                            t(`: Element menu {{pos}} z {{size}} - `, {
                                                name: choise.title,
                                                pos: i + 1,
                                                size: choises?.length,
                                                dot: ".",
                                            })
                                        }
                                        // onFocus={(e) => {
                                        onFocusVisible={(e) => {
                                            setSHOW_INFO(true);
                                        }}
                                        onBlur={() => setSHOW_INFO(false)}
                                    >
                                        <span className={classes.srOnly}>
                                            {` - `}
                                            {t("Element menu")}
                                        </span>
                                        {choise.title}
                                        <span className={classes.srOnly}>
                                            {t(`: {{pos}} z {{size}} - `, {
                                                name: choise.title,
                                                pos: i + 1,
                                                size: choises?.length,
                                                dot: ".",
                                            })}
                                        </span>

                                        <IconButton
                                            size={"small"}
                                            // onClick={(e) => handleClick(e, choise)}
                                            sx={{ ml: 2 }}
                                            tabIndex={-1}
                                            component={"span"}
                                            role={"presentation"}
                                            aria-hidden={true}
                                            disableRipple
                                        >
                                            {!!_selected ? (
                                                <Remove color={"primary"} fontSize={"small"} />
                                            ) : (
                                                <Add color={"primary"} fontSize={"small"} />
                                            )}
                                        </IconButton>
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Box>

                    <Collapse in={SHOW_INFO}>
                        <Alert
                            severity={"info"}
                            sx={{ width: "100%", mb: 1, mt: 1 }}
                            id={"addlevel-note"}
                            role="none"
                            aria-label={t(
                                ` - Użyj klawiszy strzałek (góra i dół) do nawigowania po liście, naciśnij spację lub enter, aby zaznaczyć lub odznaczyć poziom{{dot}}`,
                                {
                                    dot: ".",
                                }
                            )}
                        >
                            {t(
                                `Użyj klawiszy strzałek (góra i dół) do nawigowania po liście{{dot}} Naciśnij spację lub enter, aby zaznaczyć lub odznaczyć poziom{{dot}}`,
                                { dot: ".", colon: ":" }
                            )}
                        </Alert>
                    </Collapse>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 2,
                            mb: "11px",
                            mt: SHOW_INFO ? "16px" : "10px",
                        }}
                    >
                        <Button
                            size={"small"}
                            className={classes.button}
                            onClick={handleClose}
                            aria-label={t("Anuluj zmiany i zamknij okno")}
                        >
                            {t("Anuluj")}
                        </Button>
                        <Button
                            ref={applyRef}
                            size={"small"}
                            className={classes.button}
                            variant={"contained"}
                            onClick={handleApply}
                            disabled={JSON.stringify(selected) === JSON.stringify(prevSelected)}
                            aria-label={t("Zatwierdź zmiany i zamknij okno")}
                        >
                            {t("Zatwierdź")}
                        </Button>
                    </Box>
                </>
            </Popover>
        </>
    );
};

export default AddLevels;
